// Request schemas of the Auftraggeber endpoints (spec §1.12). They live in src/shared so the React client
// validates with the same objects; the German messages here are what the user reads on the form.
import { z } from 'zod'

export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format JJJJ-MM-TT erwartet.')

export const addressSchema = z.object({
  street: z.string().trim().min(1, 'Straße fehlt.').max(120),
  zip: z.string().trim().min(1, 'PLZ fehlt.').max(16),
  city: z.string().trim().min(1, 'Ort fehlt.').max(120),
  country: z.string().trim().regex(/^[A-Z]{2}$/, 'Land als zweistelliges Kürzel, z. B. DE.'),
})
export type Address = z.infer<typeof addressSchema>

const emailSchema = z.string().trim().max(200).email('Bitte eine gültige E-Mail-Adresse angeben.')
const optionalText = (max: number) => z.string().trim().max(max).nullish()

export const tenantSlugSchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9-]{1,31}$/, 'Kürzel: 2–32 Zeichen aus a–z, 0–9 und Bindestrich, beginnend mit einem Buchstaben.')

export const receiptPrefixSchema = z
  .string()
  .trim()
  .regex(/^[A-Z][A-Z0-9]{0,7}$/, 'Beleg-Präfix: 1–8 Großbuchstaben oder Ziffern.')

export const createTenantSchema = z.object({
  slug: tenantSlugSchema,
  name: z.string().trim().min(1, 'Name fehlt.').max(200),
  legal_name: z.string().trim().min(1, 'Firmierung fehlt.').max(200),
  address: addressSchema,
  vat_id: optionalText(32),
  tax_number: optionalText(32),
  handelsregister: optionalText(120),
  geschaeftsfuehrer: optionalText(200),
  contact_email: emailSchema,
  billing_email: emailSchema,
  reply_to_email: emailSchema,
  payment_terms_days: z.number().int().min(0).max(90).optional(),
  receipt_prefix: receiptPrefixSchema.optional(),
  accepts_pdf_invoices_at: isoDateSchema.optional(),
})
export type CreateTenantBody = z.infer<typeof createTenantSchema>

export const updateTenantSchema = z
  .object({
    name: z.string().trim().min(1, 'Name fehlt.').max(200),
    legal_name: z.string().trim().min(1, 'Firmierung fehlt.').max(200),
    address: addressSchema,
    vat_id: optionalText(32),
    tax_number: optionalText(32),
    handelsregister: optionalText(120),
    geschaeftsfuehrer: optionalText(200),
    contact_email: emailSchema,
    billing_email: emailSchema,
    reply_to_email: emailSchema,
    payment_terms_days: z.number().int().min(0).max(90),
    receipt_prefix: receiptPrefixSchema,
    account_expense: z.number().int().min(1).max(99_999_999),
    account_bank: z.number().int().min(1).max(99_999_999),
    kreditor_start: z.number().int().min(1).max(99_999_999),
    accepts_pdf_invoices_at: isoDateSchema,
    line_customer_format: z.enum(['full_name', 'initials_contact_id']),
    host_feld: z.enum(['host', 'sonstige']),
    provisionen_bestaetigen: z.number().int().min(0).max(1),
    extern_tags: z.string().trim().max(500),
    hl_date_format: z.literal('d/m/yyyy'),
    hl_number_format: z.enum(['en-US', 'de-DE']),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Es wurde nichts geändert.' })
export type UpdateTenantBody = z.infer<typeof updateTenantSchema>

/** POST /api/admin/tenants/:tenantId/invite-owner — the first admin of a freshly created Auftraggeber. */
export const inviteOwnerSchema = z.object({
  email: emailSchema,
  role: z.enum(['owner', 'admin']).optional(),
})
export type InviteOwnerBody = z.infer<typeof inviteOwnerSchema>

/** POST /api/admin/tenants/:tenantId/link-contractor — the platform path that needs no invitation. */
export const linkContractorSchema = z.object({
  contractor_id: z.string().trim().min(3).max(64),
  short_code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9_]{2,32}$/, 'Kürzel: 2–32 Großbuchstaben, Ziffern und _.')
    .optional(),
  legacy_name: optionalText(200),
})
export type LinkContractorBody = z.infer<typeof linkContractorSchema>
